/* ==========================================================================
   GSAP Animation System - Bypasses ALL Windows Animation Settings
   Asian American Forward Website
   ========================================================================== */

// Import GSAP as ES module
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

let gsapLoaded = false;

/* ==========================================================================
   GSAP Detection and Initialization
   ========================================================================== */

export function initializeGSAPAnimations() {
  console.log('🎬 Initializing GSAP Animation System...');
  
  // Check if GSAP is available (should be available since we import it)
  try {
    if (typeof gsap === 'undefined' || !gsap) {
      console.error('❌ GSAP not found. Please ensure GSAP is installed: npm install gsap');
      return false;
    }
    
    // Test GSAP functionality
    gsap.set(document.body, { opacity: 1 });
    
    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);
    
    gsapLoaded = true;
    console.log('✅ GSAP Animation System Ready - Windows settings bypassed');
    console.log('🎬 GSAP Version:', gsap.version);
    console.log('🎬 ScrollTrigger plugin registered');
  } catch (error) {
    console.error('❌ GSAP import error:', error);
    console.error('Make sure GSAP is installed: npm install gsap');
    return false;
  }
  
  // Add CSS class to disable conflicting CSS animations
  document.body.classList.add('gsap-active');
  
  // Set global GSAP defaults for better performance
  gsap.defaults({
    ease: "power2.out",
    duration: 0.6
  });
  
  // Initialize only non-hero animations immediately
  setupHoverEffects();
  setupButtonAnimations();
  setupTextTicker();
  setupCardAnimations(); // Call directly
  // setupScrollingLines(); // Disabled - using JavaScript version instead
  setupLoadingAnimations();
  setupAboutImageAnimation();
  setupMissionImageAnimation();
  
  // Wait for GSAP to be loaded and ready before starting hero animations
  window.addEventListener('gsapLoadedAndReady', () => {
    console.log('🎬 Starting hero animations after GSAP is ready');
    setupHeroAnimations();
  });
  
  return true;
}

/* ==========================================================================
   Hero Section Animations
   ========================================================================== */

function setupHeroAnimations() {
  // Hero text entrance animations - ensure consistent alignment
  const heroLines = document.querySelectorAll('.hero-line');
  if (heroLines.length > 0) {
    gsap.set(heroLines, { 
      opacity: 0,
      transformOrigin: "left center",
      transform: "none" // Reset any CSS transforms
    });
    
    gsap.to(heroLines, {
      opacity: 1,
      duration: 0.8,
      stagger: 0.2,
      ease: "power3.out",
      delay: 0.2
    });
  }
  
  // Hero description
  const heroDescription = document.querySelector('.hero-description');
  if (heroDescription) {
    gsap.set(heroDescription, { y: 30, opacity: 0 });
    gsap.to(heroDescription, {
      y: 0,
      opacity: 1,
      duration: 0.8,
      delay: 0.8,
      ease: "power2.out"
    });
  }
  
  // Hero actions
  const heroActions = document.querySelector('.hero-actions');
  if (heroActions) {
    gsap.set(heroActions, { y: 30, opacity: 0 });
    gsap.to(heroActions, {
      y: 0,
      opacity: 1,
      duration: 0.8,
      delay: 1.0,
      ease: "power2.out"
    });
  }
}

/* ==========================================================================
   Card Animations
   ========================================================================== */

function setupCardAnimations() {
  console.log('🎬 Setting up card animations...');
  // Hero cards entrance - reset CSS positioning and use GSAP
  const heroCards = document.querySelectorAll('.hero-card');
  if (heroCards.length > 0) {
    heroCards.forEach((card, index) => {
      // Clear any CSS transforms that might interfere
      card.style.transform = '';
      
      // Set initial GSAP state
      gsap.set(card, { 
        y: 50, 
        opacity: 0,
        clearProps: "transform" // Clear CSS transforms
      });
    });
    
    gsap.to(heroCards, {
      y: 0,
      opacity: 1,
      duration: 0.6,
      stagger: 0.2,
      delay: 0.8,
      ease: "back.out(1.7)",
      onComplete: setupCardOpacities
    });
  }
  
  // Modal event cards
  const modalEventCards = document.querySelectorAll('.modal-event-card');
  modalEventCards.forEach(card => {
    // Hover animations - no scaling, just lift
    card.addEventListener('mouseenter', () => {
      gsap.to(card, {
        y: -8,
        duration: 0.3,
        ease: "power2.out"
      });
    });
    
    card.addEventListener('mouseleave', () => {
      gsap.to(card, {
        y: 0,
        duration: 0.3,
        ease: "power2.out"
      });
    });
  });
}

function setupCardOpacities() {
  // Set all cards to fully opaque
  const heroCards = document.querySelectorAll('.hero-card');
  heroCards.forEach(card => {
    gsap.to(card, { opacity: 1, duration: 0.3 });
  });
}

/* ==========================================================================
   About Image Animation
   ========================================================================== */

function setupAboutImageAnimation() {
  const aboutImage = document.querySelector('.about-img');
  const aboutContainer = document.querySelector('.about-img-container');
  
  if (!aboutImage || !aboutContainer) return;
  
  // Set initial state
  gsap.set(aboutImage, {
    opacity: 0,
    scale: 0.8,
    y: 30
  });
  
  // Animate in when page loads
  gsap.to(aboutImage, {
    opacity: 1,
    scale: 1,
    y: 0,
    duration: 0.8,
    delay: 0.5,
    ease: "power2.out"
  });
}

function setupMissionImageAnimation() {
  const missionImage = document.querySelector('.mission-img');
  const missionContainer = document.querySelector('.mission-img-container');
  
  if (!missionImage || !missionContainer) return;
  
  // Set initial state
  gsap.set(missionImage, {
    opacity: 0,
    scale: 0.8,
    y: 30
  });
  
  // Animate in when page loads
  gsap.to(missionImage, {
    opacity: 1,
    scale: 1,
    y: 0,
    duration: 0.8,
    delay: 0.7,
    ease: "power2.out"
  });
}

/* ==========================================================================
   3D Card Hover Effects
   ========================================================================== */

export function setup3DCardHover(card) {
  if (!gsapLoaded) {
    console.warn('GSAP not loaded, skipping 3D hover setup');
    return;
  }
  
  console.log('✨ Setting up 3D hover for card:', card);
  
  let hoverTimeout = null; // Track hover timeout
  
  card.addEventListener('mouseenter', () => {
    const stackIndex = parseInt(card.getAttribute('data-card-index')) || 0;
    if (stackIndex !== 0) return; // Only top card
    
    console.log('🎯 Card hover started');
    
    // Clear any pending resume timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }
    
    // Immediately pause card cycling when hovering
    window.dispatchEvent(new CustomEvent('pauseCardCycling'));
    
    gsap.to(card, {
      rotationX: 0,
      rotationY: 0,
      scale: 1, // No zoom effect - keep at normal scale
      z: 10, // Slight lift without zoom
      zIndex: 100,
      duration: 0.3,
      ease: "power2.out"
    });
  });
  
  card.addEventListener('mouseleave', () => {
    const stackIndex = parseInt(card.getAttribute('data-card-index')) || 0;
    if (stackIndex !== 0) return;
    
    console.log('🎯 Card hover ended');
    
    // Reset the card to neutral position first
    gsap.to(card, {
      rotationX: 0,
      rotationY: 0,
      scale: 1, // Reset scale
      z: 0,
      zIndex: 40, // Reset to original z-index
      duration: 0.4,
      ease: "power2.out"
    });
    
    // Resume card cycling after a delay (1 second)
    hoverTimeout = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('resumeCardCycling'));
      hoverTimeout = null;
    }, 1000);
  });
  
  card.addEventListener('mousemove', (e) => {
    const stackIndex = parseInt(card.getAttribute('data-card-index')) || 0;
    if (stackIndex !== 0) return;
    
    const rect = card.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const mouseX = (e.clientX - centerX) / (rect.width / 2);
    const mouseY = (e.clientY - centerY) / (rect.height / 2);
    
    const rotationX = mouseY * -1.5;
    const rotationY = mouseX * 1.5;
    
    gsap.to(card, {
      rotationX: rotationX,
      rotationY: rotationY,
      scale: 1, // No zoom during movement
      z: 15, // Slight lift during movement
      zIndex: 100,
      duration: 0.1,
      ease: "none"
    });
  });
}

/* ==========================================================================
   Button Animations
   ========================================================================== */

function setupButtonAnimations() {
  // Rainbow border animation disabled - using simple black border instead
  const donateButton = document.querySelector('.nav-link--primary');
  if (donateButton) {
    console.log('🚫 Donate button rainbow animation disabled - using simple black border');
    
    // Remove any existing animation classes
    donateButton.classList.remove('gsap-controlled');
    
    // Ensure simple black border
    donateButton.style.borderColor = '#000000';
    donateButton.style.removeProperty('box-shadow');
    
    // Keep the lift effect on hover but no color animation
    donateButton.addEventListener('mouseenter', () => {
      gsap.to(donateButton, {
        y: -2,
        duration: 0.2,
        ease: "power2.out"
      });
    });
    
    donateButton.addEventListener('mouseleave', () => {
      gsap.to(donateButton, {
        y: 0,
        duration: 0.2,
        ease: "power2.out"
      });
    });
  } else {
    console.warn('❌ Donate button not found - animation cannot be initialized');
  }
}

/* ==========================================================================
   Hover Effects
   ========================================================================== */

function setupHoverEffects() {
  // Generic hover lift effects
  const hoverElements = document.querySelectorAll('.hover-lift');
  hoverElements.forEach(element => {
    element.addEventListener('mouseenter', () => {
      gsap.to(element, {
        y: -4,
        duration: 0.2,
        ease: "power2.out"
      });
    });
    
    element.addEventListener('mouseleave', () => {
      gsap.to(element, {
        y: 0,
        duration: 0.2,
        ease: "power2.out"
      });
    });
  });
  
  // Generic hover scale effects
  const scaleElements = document.querySelectorAll('.hover-scale');
  scaleElements.forEach(element => {
    element.addEventListener('mouseenter', () => {
      gsap.to(element, {
        scale: 1.05,
        duration: 0.2,
        ease: "power2.out"
      });
    });
    
    element.addEventListener('mouseleave', () => {
      gsap.to(element, {
        scale: 1,
        duration: 0.2,
        ease: "power2.out"
      });
    });
  });
  
  // Image hover effects
  const images = document.querySelectorAll('.about-img, .mission-img');
  images.forEach(img => {
    img.addEventListener('mouseenter', () => {
      gsap.to(img, {
        y: -4,
        scale: 1.02,
        duration: 0.3,
        ease: "power2.out"
      });
    });
    
    img.addEventListener('mouseleave', () => {
      gsap.to(img, {
        y: 0,
        scale: 1,
        duration: 0.3,
        ease: "power2.out"
      });
    });
  });
}

/* ==========================================================================
   Text Ticker Animation
   ========================================================================== */

function setupTextTicker() {
  // Handle new diagonal separator
  const separatorContainer = document.querySelector('.diagonal-separator');
  const scrollingText = document.querySelector('.scrolling-text');
  const scrollingNames = document.querySelector('.scrolling-names');
  
  if (!separatorContainer || !scrollingText || !scrollingNames) {
    console.log('Diagonal separator elements not found, skipping GSAP animation');
    return;
  }
  
  console.log('✅ Setting up GSAP for diagonal separator with seamless scrolling');
  
  // Disable CSS animation completely
  gsap.set(scrollingText, { clearProps: "animation" });
  
  let tl; // Main timeline
  let isHovered = false;
  
  const setupAnimation = () => {
    // Get fresh measurements
    const containerWidth = scrollingNames.offsetWidth;
    const textWidth = scrollingText.scrollWidth;
    
    // Kill existing timeline if it exists
    if (tl) tl.kill();
    
    // Since we have 2 repetitions of the same names, we only need to animate 1/2 of the text width
    // This creates a seamless loop where one cycle looks identical to the next
    const halfWidth = textWidth / 2;
    
    // Create the animation
    tl = gsap.timeline({
      repeat: -1, // Repeat indefinitely
      defaults: { ease: "none" } // Linear animation
    });

    // Animate to the left by the width of one repeating segment
    tl.fromTo(scrollingText,
      { x: 0 }, // Start at x=0
      {
        x: -halfWidth, // Animate to -halfWidth
        duration: 10, // Adjust duration as needed for desired speed
      }
    );
    
    console.log('🎬 Seamless separator animation setup:', { containerWidth, textWidth, halfWidth });
  };
  
  // Initial setup
  setupAnimation();
  
  // Simple hover handling
  separatorContainer.addEventListener('mouseenter', () => {
    if (!isHovered && tl) {
      console.log('🎬 Pausing separator animation on hover');
      isHovered = true;
      tl.pause();
    }
  });
  
  separatorContainer.addEventListener('mouseleave', () => {
    if (isHovered && tl) {
      console.log('🎬 Resuming separator animation');
      isHovered = false;
      tl.resume();
    }
  });
  
  // Handle window resize with debounce
  let resizeTimeout;
  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      console.log('🔄 Resizing separator animation');
      setupAnimation();
    }, 250);
  };
  
  window.addEventListener('resize', handleResize);
}

/* ==========================================================================
   Loading Animations
   ========================================================================== */

function setupLoadingAnimations() {
  // Loading screen animations - ensure proper alignment regardless of Windows settings
  const loadingScreen = document.getElementById('loading-screen');
  if (loadingScreen) {
    const loadingLines = document.querySelectorAll('.loading-line');
    if (loadingLines.length > 0) {
      // Force consistent initial state and alignment
      gsap.set(loadingLines, { 
        x: 50, 
        opacity: 0,
        transformOrigin: "left center"
      });
      
      gsap.to(loadingLines, {
        x: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.3,
        delay: 0.2,
        ease: "power3.out"
      });
    }
  }
  
  // Loading spinner
  const spinners = document.querySelectorAll('.loading-spinner');
  spinners.forEach(spinner => {
    gsap.to(spinner, {
      rotation: 360,
      duration: 1,
      repeat: -1,
      ease: "none"
    });
  });
  
  // Pulse animations
  const pulseElements = document.querySelectorAll('.animate-pulse, .notification-icon');
  pulseElements.forEach(element => {
    gsap.to(element, {
      opacity: 0.7,
      duration: 1,
      repeat: -1,
      yoyo: true,
      ease: "power2.inOut"
    });
  });
}

/* ==========================================================================
   Navigation Animations
   ========================================================================== */

export function setupMobileMenuAnimation() {
  // DISABLED: Navigation now uses pure CSS for maximum compatibility and reliability
  // This prevents conflicts between GSAP and CSS animations
  console.log('📱 GSAP Mobile menu animation disabled - using CSS-only for better Windows compatibility');
  return;
}

/* ==========================================================================
   Scrolling Lines Animation (GSAP Version)
   ========================================================================== */

function setupScrollingLines() {
  console.log('🎬 Setting up GSAP scrolling lines...');
  
  // Disable GSAP for scrolling lines - let JavaScript version handle it
  console.log('🔄 Skipping GSAP scrolling lines - using JavaScript fallback for better control');
  return;
  
  const diagonalSeparator = document.querySelector('.diagonal-separator');
  
  if (!diagonalSeparator) {
    console.log('Diagonal separator not found, skipping GSAP scrolling lines');
    return;
  }
  
  console.log('✅ Found diagonal separator, setting up lines...');
  
  // Wait for the JavaScript version to create the lines first
  const checkForLines = () => {
    const lines = document.querySelectorAll('.diagonal-line');
    
    if (lines.length === 0) {
      console.log('⏳ Lines not created yet, waiting...');
      setTimeout(checkForLines, 100);
      return;
    }
    
    console.log('✅ Found scrolling lines, setting up GSAP animation for', lines.length, 'lines');
    
    // Simple scroll trigger right after separator
    ScrollTrigger.create({
      trigger: diagonalSeparator,
      start: "bottom top", // When separator bottom hits top of viewport
      end: "bottom+=300% top", // Long animation distance
      scrub: 1,
      onUpdate: (self) => {
        console.log('GSAP scroll progress:', self.progress);
        
        lines.forEach((line, index) => {
          const color = line.getAttribute('data-color');
          const staggeredProgress = Math.max(0, self.progress - (index * 0.1));
          
          if (staggeredProgress > 0) {
            gsap.set(line, {
              opacity: Math.min(0.9, staggeredProgress),
              scaleY: staggeredProgress,
              rotation: -130,
              x: window.innerWidth * 0.7 + (index * 50),
              y: window.innerHeight * staggeredProgress,
              width: Math.min(window.innerWidth, 200 + (staggeredProgress * 500)),
              height: 63,
              backgroundColor: color === 'red' ? '#8f2226' : (color === 'blue' ? '#084874' : '#000000')
            });
          }
        });
      }
    });
    
    console.log('🎬 GSAP scrolling lines setup complete');
  };
  
  checkForLines();
}

/* ==========================================================================
   Utility Functions
   ========================================================================== */

export function animateIn(element, options = {}) {
  if (!gsapLoaded) return Promise.resolve();
  
  const {
    direction = 'up',
    distance = 30,
    duration = 0.6,
    delay = 0,
    ease = "power2.out"
  } = options;
  
  const fromProps = { opacity: 0 };
  const toProps = { opacity: 1, duration, delay, ease };
  
  switch (direction) {
    case 'up':
      fromProps.y = distance;
      toProps.y = 0;
      break;
    case 'down':
      fromProps.y = -distance;
      toProps.y = 0;
      break;
    case 'left':
      fromProps.x = distance;
      toProps.x = 0;
      break;
    case 'right':
      fromProps.x = -distance;
      toProps.x = 0;
      break;
  }
  
  gsap.set(element, fromProps);
  return gsap.to(element, toProps);
}

export function animateOut(element, options = {}) {
  if (!gsapLoaded) return Promise.resolve();
  
  const {
    direction = 'up',
    distance = 30,
    duration = 0.3,
    ease = "power2.in"
  } = options;
  
  const toProps = { opacity: 0, duration, ease };
  
  switch (direction) {
    case 'up':
      toProps.y = -distance;
      break;
    case 'down':
      toProps.y = distance;
      break;
    case 'left':
      toProps.x = -distance;
      break;
    case 'right':
      toProps.x = distance;
      break;
  }
  
  return gsap.to(element, toProps);
}

export function isGSAPLoaded() {
  return gsapLoaded;
}

console.log('🎯 GSAP Animation System Loaded - Windows Animation Settings Completely Bypassed'); 